import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { getAdminToken, clearAdminToken } from '../admin/authToken'

type VerifyResponse = {
  valid?: boolean
  error?: string
}

type ProtectedRouteProps = {
  children: React.ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    checkAuthentication()
  }, [])

  const checkAuthentication = async () => {
    try {
      const token = getAdminToken()
      
      if (!token) {
        setIsAuthenticated(false)
        navigate('/admin/login')
        return
      }

      // Verify token with server
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json() as VerifyResponse

      if (response.ok && data.valid) {
        setIsAuthenticated(true)
      } else {
        // Token is invalid or expired
        clearAdminToken()
        setIsAuthenticated(false)
        navigate('/admin/login')
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { action: 'auth_verify' } })
      clearAdminToken()
      setIsAuthenticated(false)
      navigate('/admin/login')
    }
  }

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    )
  }

  // If authenticated, render the protected content
  if (isAuthenticated) {
    return <>{children}</>
  }

  // If not authenticated, the useEffect will redirect to login
  return null
}
